/**
 * İÇERİK DETAY SAYFASI — Yazar Motoru çıktısı ayrı sayfada:
 * kapak İÇERİĞİN ÜSTÜNDE (gövdede tekrar görünmez), otomatik SEO/GEO/AEO checklist,
 * meta bilgiler, link planı, GEO taktikleri ve tam içerik.
 */
import { useState } from "react";
import { Link, useRoute } from "wouter";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { useOnay } from "@/components/ui/onay-dialog";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { WriterMd } from "@/components/writer-md";
import { ArrowLeft, Check, Loader2, Sparkles, Target, RefreshCw, Download } from "lucide-react";

// Markdown → HTML (dışa aktarma için: başlık/kalın/link/liste/tablo/görsel)
function mdToHtml(md: string, images?: Record<string, { url: string; alt: string }>, coverUrl?: string | null, coverAlt?: string): string {
  const esc = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const abs = (u: string) => (u.startsWith("/") ? window.location.origin + u : u);
  const inline = (s: string) => esc(s)
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>')
    .replace(/\[GÖRSEL-(\d+)\]/g, (_, n) => { const im = images?.[`[GÖRSEL-${n}]`]; return im ? `<img src="${abs(im.url)}" alt="${esc(im.alt)}" style="max-width:100%">` : ""; });
  const lines = md.split("\n");
  const out: string[] = [];
  if (coverUrl) out.push(`<p><img src="${abs(coverUrl)}" alt="${esc(coverAlt ?? "")}" style="max-width:100%"></p>`);
  let list: string[] = []; let table: string[][] = [];
  const flushList = () => { if (list.length) { out.push("<ul>" + list.map((l) => `<li>${inline(l)}</li>`).join("") + "</ul>"); list = []; } };
  const flushTable = () => {
    if (table.length) {
      const [h, ...body] = table;
      out.push('<table border="1" cellpadding="6" cellspacing="0" style="border-collapse:collapse"><thead><tr>' + h.map((c) => `<th>${inline(c)}</th>`).join("") + "</tr></thead><tbody>" + body.map((r) => "<tr>" + r.map((c) => `<td>${inline(c)}</td>`).join("") + "</tr>").join("") + "</tbody></table>");
      table = [];
    }
  };
  for (const line of lines) {
    const solo = line.trim().match(/^\[GÖRSEL-(\d+)\]$/);
    if (solo) { flushList(); flushTable(); const im = images?.[`[GÖRSEL-${solo[1]}]`]; if (im) out.push(`<p><img src="${abs(im.url)}" alt="${esc(im.alt)}" style="max-width:100%"></p>`); continue; }
    if (/^\s*\|.*\|\s*$/.test(line)) { flushList(); const cells = line.trim().replace(/^\||\|$/g, "").split("|").map((c) => c.trim()); if (!cells.every((c) => /^:?-{2,}:?$/.test(c) || c === "")) table.push(cells); continue; }
    flushTable();
    const h = line.match(/^(#{1,4})\s+(.*)/);
    if (h) { flushList(); out.push(`<h${h[1].length}>${inline(h[2])}</h${h[1].length}>`); continue; }
    if (/^\s*[-*]\s+/.test(line)) { list.push(line.replace(/^\s*[-*]\s+/, "")); continue; }
    flushList();
    // Kavram/uzman kutusu (alıntı bloğu) → stilli blockquote
    if (line.trim().startsWith(">")) { out.push(`<blockquote style="margin:12px 0;padding:10px 14px;border-left:4px solid #f59e0b;background:#fdf6ec"><p style="margin:4px 0">${inline(line.trim().replace(/^>\s?/, ""))}</p></blockquote>`); continue; }
    if (line.trim()) out.push(`<p>${inline(line)}</p>`);
  }
  flushList(); flushTable();
  return out.join("\n");
}

const C = { cyan: "#e83e3c", green: "#12b76a", red: "#f04438", amber: "#f79009", purple: "#e83e3c", blue: "#ec6461" };
const fmt = (n: number) => (n >= 10000 ? `${(n / 1000).toFixed(0)}k` : n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(Math.round(n)));
const shortUrl = (u: string) => (u || "").replace(/^https?:\/\/[^/]+/, "").replace(/\/$/, "") || "/";

type ImageBriefT = { position: string; prompt: string; altText: string; aspectRatio: string; placeholder: string; imageUrl?: string | null; imageError?: string | null };
type WriterResultT = {
  title: string; metaDescription: string; slug: string; content: string;
  imageBriefs: ImageBriefT[]; toneUsed: string;
  linkPlan: { anchor: string; url: string; reason: string }[];
  serpCompetitors: { url: string; title: string; strengths: string[] }[];
  geoTactics: string[]; schemaSuggestion: string;
  audit: { score: number; passed: boolean; fixedIssues: number; notes: string[] } | null;
  wordCount: number;
  contentScore?: { our: number; competitorAvg: number; target: number; termCoverage: number; missingTerms: { term: string; competitorsUsing: number; ourCount: number }[]; competitorScores: { domain: string; score: number }[] } | null;
  plagiarism?: { checked: number; hits: { sentence: string; url: string; title: string }[] } | null;
  keywordDensity?: { gecis: number; kelimeSayisi: number; yuzde: number; hedefGecis: number; durum: "dusuk" | "iyi" | "yuksek" } | null;
};
type WriterRow = {
  id: number; domainId: number;
  brief: { title: string; targetKeyword: string; volume: number; clusterName: string | null; supportingKeywords: string[] };
  result: WriterResultT | null; status: string; writtenAt: string | null;
  running?: boolean; runError?: string | null;
};

type CheckItem = { group: "SEO" | "GEO" | "AEO"; label: string; ok: boolean; detail?: string };

// Otomatik SEO/GEO/AEO checklist — üretilen içerikten hesaplanır, elle işaretleme yok
function buildChecklist(w: WriterRow): CheckItem[] {
  const r = w.result!;
  const kw = (w.brief.targetKeyword || "").toLowerCase();
  const kwHead = kw.split(" ")[0] ?? kw;
  const content = r.content || "";
  const h2s = (content.match(/^##\s+/gm) || []).length;
  const firstPara = content.split("\n").find((l) => l.trim() && !l.startsWith("#") && !l.trim().startsWith("[GÖRSEL")) ?? "";
  const questionHeads = (content.match(/^#{2,3}\s+.+\?\s*$/gm) || []).length;
  const hasFaq = /^#{2,3}\s+.*(sss|sıkça sorulan|faq|sorular)/im.test(content) || questionHeads >= 3;
  const imgsOk = r.imageBriefs.filter((im) => im.imageUrl).length;
  return [
    { group: "SEO", label: "Başlık uzunluğu 30–65 karakter", ok: r.title.length >= 30 && r.title.length <= 65, detail: `${r.title.length} kr` },
    { group: "SEO", label: "Meta açıklama 140–160 karakter", ok: r.metaDescription.length >= 140 && r.metaDescription.length <= 160, detail: `${r.metaDescription.length} kr` },
    // Kelime yoğunluğu SUNUCUDA ölçülür (Rank Math formülü, Türkçe ek toleranslı) — eski üretimlerde alan yoktur
    ...(r.keywordDensity ? [{ group: "SEO" as const, label: "Kelime yoğunluğu %1–1.5 (ajans standardı)", ok: r.keywordDensity.durum === "iyi", detail: `%${r.keywordDensity.yuzde} · ${r.keywordDensity.gecis} geçiş` }] : []),
    { group: "SEO", label: "Ana kelime başlıkta", ok: r.title.toLowerCase().includes(kwHead) },
    { group: "SEO", label: "Ana kelime giriş paragrafında", ok: firstPara.toLowerCase().includes(kwHead) },
    { group: "SEO", label: "En az 4 H2 başlık", ok: h2s >= 4, detail: `${h2s} H2` },
    { group: "SEO", label: "İç link inşası (≥3, cluster korumalı)", ok: r.linkPlan.length >= 3, detail: `${r.linkPlan.length} link` },
    { group: "SEO", label: "Görseller üretildi ve yerleşti", ok: imgsOk > 0, detail: `${imgsOk}/${r.imageBriefs.length}` },
    // Kelime hedefi 500-700'e çekildi (kullanıcı: "çok uzun yazıyoruz") — eşik 800 değil 450
    { group: "SEO", label: "Kelime hacmi yeterli (≥450)", ok: r.wordCount >= 450, detail: `${fmt(r.wordCount)} kelime` },
    { group: "GEO", label: "GEO taktikleri uygulandı (≥3)", ok: r.geoTactics.length >= 3, detail: `${r.geoTactics.length} taktik` },
    { group: "GEO", label: "Schema (yapılandırılmış veri) önerisi", ok: !!r.schemaSuggestion?.trim() },
    { group: "GEO", label: "SERP rakipleri incelendi", ok: r.serpCompetitors.length > 0, detail: `${r.serpCompetitors.length} rakip` },
    { group: "GEO", label: "Kalite denetçisi skoru ≥ 85", ok: (r.audit?.score ?? 0) >= 85, detail: `${r.audit?.score ?? "—"}/100` },
    { group: "AEO", label: "Soru formatlı başlıklar (AI'ın alıntılayabileceği)", ok: questionHeads >= 2, detail: `${questionHeads} soru başlığı` },
    { group: "AEO", label: "SSS/FAQ bölümü", ok: hasFaq },
    { group: "AEO", label: "Tablo/liste yapıları (özetlenebilirlik)", ok: /^\s*\|.*\|/m.test(content) || (content.match(/^\s*[-*]\s+/gm) || []).length >= 5 },
    { group: "AEO", label: "Destek kelimeler işlendi", ok: (w.brief.supportingKeywords ?? []).filter((s) => content.toLowerCase().includes(s.toLowerCase().split(" ")[0] ?? "")).length >= Math.min(2, (w.brief.supportingKeywords ?? []).length) },
  ];
}

export default function ContentDetail() {
  const [, params] = useRoute("/content/:id");
  const briefId = Number(params?.id ?? 0);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { onayIste, OnayPenceresi } = useOnay();
  const [copied, setCopied] = useState<string | null>(null);
  /* UYGULAMA KOLU (Faz 6): içeriği WordPress'e taslak/yayın gönder */
  const [wpBusy, setWpBusy] = useState(false);
  const [showImgDetails, setShowImgDetails] = useState(false);
  const [tab, setTab] = useState<"icerik" | "analiz">("icerik"); // sekmeler: içerik | brief & analiz

  const { data: w } = useQuery({
    queryKey: ["writer-brief", briefId],
    enabled: briefId > 0,
    refetchInterval: (q) => ((q.state.data as WriterRow | undefined)?.running ? 5000 : false),
    queryFn: async (): Promise<WriterRow | null> => {
      const r = await fetch(`/api/writer-briefs/${briefId}`);
      return r.ok ? r.json() : null;
    },
  });

  const wpDurum = useQuery<{ bagli: boolean }>({
    queryKey: ["wp-durum", w?.domainId],
    enabled: !!w?.domainId,
    staleTime: 60_000,
    queryFn: async () => (await fetch(`/api/domains/${w!.domainId}/wp`)).json(),
  });
  const wpGonder = async (yayinla: boolean) => {
    if (!w?.domainId || wpBusy) return;
    if (yayinla && !(await onayIste({ baslik: "Doğrudan yayınlansın mı?", aciklama: "İçerik WordPress'te taslak olarak değil, DOĞRUDAN YAYINDA oluşturulacak. Genelde önce taslak gönderip WP'de son kontrolü yapmak daha güvenlidir.", onayYazisi: "Yayınla", tehlikeli: true }))) return;
    setWpBusy(true);
    try {
      const r = await fetch(`/api/domains/${w.domainId}/wp/icerik-gonder`, {
        method: "POST", headers: { "content-type": "application/json" },
        body: JSON.stringify({ briefId, yayinla }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error ?? "Gönderilemedi");
      // Dil uyarısı varsa ASIL mesaj odur (içerik yanlış dilde göründüyse kullanıcı hemen bilsin)
      if (j.dilUyarisi) toast({ title: "⚠️ Dil yerleştirilemedi", description: String(j.dilUyarisi), variant: "destructive" });
      toast({ title: yayinla ? "Yayınlandı ✓" : "Taslak olarak gönderildi ✓", description: `${j.gorselSayisi ?? 0} görsel yüklendi.${j.dil ? ` Dil: ${String(j.dil).toUpperCase()}.` : ""}${j.metaGuncellendi ? " SEO meta dahil." : " SEO meta alanını site kabul etmedi — WP'de elle girin."}${j.wpLink ? ` → ${j.wpLink}` : ""}` });
    } catch (e) { toast({ title: "Gönderilemedi", description: String(e instanceof Error ? e.message : e), variant: "destructive" }); }
    finally { setWpBusy(false); }
  };

  const copyText = async (key: string, text: string) => {
    try { await navigator.clipboard.writeText(text); setCopied(key); setTimeout(() => setCopied(null), 1500); } catch { /* pano izni yok */ }
  };

  // İçerik Takvimi bağlantısı — başlık + kelimeler + meta ile plana eklenir
  const [addedCal, setAddedCal] = useState(false);
  // ZAMANLI WP YAYINI: takvime eklerken tarih + yayın modu seçilir (kullanıcı isteği:
  // "Cuma'ya planladım, zamanı gelince paylaşsın"). Varsayılan: onaylı (önerilen).
  const [calAcik, setCalAcik] = useState(false);
  const [calTarih, setCalTarih] = useState(() => new Date().toISOString().slice(0, 10));
  const [calMod, setCalMod] = useState<"kapali" | "onayli" | "otomatik">("onayli");
  const [calBusy, setCalBusy] = useState(false);
  const addToCalendar = async () => {
    if (!w?.result || !w.domainId || calBusy) return;
    const r2 = w.result;
    setCalBusy(true);
    try {
    const resp = await fetch(`/api/domains/${w.domainId}/content-plans`, {
      method: "POST", headers: { "content-type": "application/json" },
      body: JSON.stringify({
        title: r2.title, plannedDate: calTarih, contentType: "blog",
        briefId, wpYayinModu: calMod === "kapali" ? null : calMod,
        coverUrl: r2.imageBriefs.find((im) => im.placeholder === "[GÖRSEL-1]" && im.imageUrl)?.imageUrl ?? undefined,
        notes: `${r2.metaDescription}\n\nSlug: /${r2.slug}\nDenetçi: ${r2.audit?.score ?? "-"}/100 · ${r2.wordCount} kelime\n(Yazar Robotu içeriği: /content/${briefId})`,
        keywords: [{ keyword: w.brief.targetKeyword, usa: 0, global: w.brief.volume, role: "ana" }, ...(w.brief.supportingKeywords ?? []).slice(0, 6).map((k) => ({ keyword: k, usa: 0, global: 0, role: "destek" }))],
      }),
    });
    if (!resp.ok) throw new Error((await resp.json().catch(() => ({}))).error ?? "Takvime eklenemedi");
    setAddedCal(true); setCalAcik(false);
    toast({ title: "Takvime eklendi ✓", description: calMod === "kapali" ? `${calTarih} için planlandı.` : calMod === "otomatik" ? `${calTarih} sabahı 09:00'da WordPress'te kendiliğinden yayınlanacak.` : `${calTarih} sabahı 09:00'da onayına sunulacak (zil + Planlama ekranı).` });
    } catch (e) { toast({ title: "Takvime eklenemedi", description: String(e instanceof Error ? e.message : e), variant: "destructive" }); }
    finally { setCalBusy(false); }
  };

  // ELLE DÜZENLEME (kullanıcı isteği): makale metni doğrudan değiştirilebilir —
  // yazar motoru çağrılmaz, ücretsizdir. Başlık + meta + gövde tek kaydetmeyle gider.
  const [duzenle, setDuzenle] = useState(false);
  const [tBaslik, setTBaslik] = useState("");
  const [tMeta, setTMeta] = useState("");
  const [tGovde, setTGovde] = useState("");
  const kaydet = useMutation({
    mutationFn: async () => {
      const r2 = await fetch(`/api/writer-briefs/${briefId}/icerik`, {
        method: "PATCH", headers: { "content-type": "application/json" },
        body: JSON.stringify({ content: tGovde, title: tBaslik, metaDescription: tMeta }),
      });
      const j = await r2.json();
      if (!r2.ok) throw new Error(j.error ?? "Kaydedilemedi");
      return j as { wordCount: number };
    },
    onSuccess: (j) => {
      toast({ title: "Kaydedildi ✓", description: `${fmt(j.wordCount)} kelime. WordPress'e göndereceksen artık bu hâli gider.` });
      setDuzenle(false);
      void queryClient.invalidateQueries({ queryKey: ["writer-brief", briefId] });
    },
    onError: (e) => toast({ title: "Kaydedilemedi", description: String(e instanceof Error ? e.message : e), variant: "destructive" }),
  });

  // Yeniden üretim + REVİZYON DÖNGÜSÜ: not verilirse yazar sıfırdan yazmaz,
  // mevcut içeriği nota göre günceller (kalan her şey korunur).
  const [revNote, setRevNote] = useState("");
  const regenerate = useMutation({
    mutationFn: async (note?: string) => {
      const r2 = await fetch(`/api/writer-briefs/${briefId}/generate`, {
        method: "POST", headers: { "content-type": "application/json" },
        body: JSON.stringify(note ? { revisionNote: note } : {}),
      });
      if (!r2.ok) throw new Error((await r2.json().catch(() => ({}))).error ?? "Üretim başlatılamadı");
      return r2.json();
    },
    onSuccess: () => { setRevNote(""); queryClient.invalidateQueries({ queryKey: ["writer-brief", briefId] }); },
  });

  const r = w?.result ?? null;
  const cover = r?.imageBriefs.find((im) => im.placeholder === "[GÖRSEL-1]" && im.imageUrl);
  // Kapak İÇERİĞİN ÜSTÜNDE gösterilir — gövdedeki [GÖRSEL-1] işaretçileri temizlenir.
  // Eski üretimlerde kalmış "Medically reviewed"/"Son güncelleme" satırları da burada ayıklanır.
  const bodyContent = (r?.content ?? "")
    .replace(/^\s*\[GÖRSEL-1\]\s*$/gm, "").replace(/\[GÖRSEL-1\]/g, "")
    .replace(/^>?\s*\**\s*(medically reviewed by|uzman incelemesi|tıbbi inceleme)\b.*$/gim, "")
    .replace(/^>?\s*\**\s*(last updated|son güncelleme)\s*:?.*$/gim, "")
    .replace(/^#{2,3}\s+(içindekiler|table of contents|contents)\s*\n(?:\s*(?:[-*]|\d+\.)\s+.*\n?)*/gim, "")
    .replace(/\n{3,}/g, "\n\n");

  // HTML / Word dışa aktarma
  const buildHtml = () => {
    if (!r) return "";
    return mdToHtml(bodyContent, bodyImages, cover?.imageUrl ?? null, cover?.altText);
  };
  const copyHtml = async () => {
    const html = buildHtml();
    try {
      if (navigator.clipboard && window.ClipboardItem) {
        await navigator.clipboard.write([new ClipboardItem({ "text/html": new Blob([html], { type: "text/html" }), "text/plain": new Blob([bodyContent], { type: "text/plain" }) })]);
      } else await navigator.clipboard.writeText(html);
      setCopied("html"); setTimeout(() => setCopied(null), 1500);
    } catch { /* pano izni yok */ }
  };
  // WORD PAKETİ SUNUCUDAN GELİR: eski tarayıcı-yapımı .doc görselleri gömemiyordu
  // (Word göreli URL çekemez → "küçücük ve bozuk"). Sunucu gerçek .docx kurar:
  // görseller dosyanın içine gömülü + görsel dosyaları + meta.txt, hepsi tek ZIP.
  const [zipBusy, setZipBusy] = useState(false);
  const downloadWord = async () => {
    if (!r || zipBusy) return;
    setZipBusy(true);
    try {
      const resp = await fetch(`/api/writer-briefs/${briefId}/export`);
      if (!resp.ok) throw new Error((await resp.json().catch(() => ({}))).error ?? "Dışa aktarma başarısız");
      const blob = await resp.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${(r.slug || "icerik").replace(/[^a-z0-9-]/g, "") || "icerik"}.zip`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      alert((e as Error).message);
    } finally { setZipBusy(false); }
  };
  const bodyImages = r ? Object.fromEntries(r.imageBriefs.filter((im) => im.imageUrl && im.placeholder !== "[GÖRSEL-1]").map((im) => [im.placeholder, { url: im.imageUrl!, alt: im.altText }])) : undefined;
  const checklist = r ? buildChecklist(w!) : [];
  const okCount = checklist.filter((c) => c.ok).length;

  return (
    <div className="px-4 sm:px-6 py-6 w-full">
      {OnayPenceresi}
      <div className="flex items-center justify-between gap-3 mb-4">
        <Link href="/content-opportunities"><Button size="sm" variant="ghost" className="gap-1.5"><ArrowLeft className="w-4 h-4" /> İçerik Üretim Ajanı</Button></Link>
        {r && (
          <div className="flex items-center gap-2 flex-wrap">
            {w?.running && <span className="text-xs inline-flex items-center gap-1.5 text-muted-foreground"><Loader2 className="w-3.5 h-3.5 animate-spin" /> yeniden üretiliyor…</span>}
            <div className="relative">
              <Button size="sm" variant={addedCal ? "outline" : "secondary"} disabled={addedCal} className="text-xs gap-1" onClick={() => setCalAcik((o) => !o)}>
                {addedCal ? <><Check className="w-3.5 h-3.5" style={{ color: C.green }} /> Takvimde</> : "📅 Takvime Ekle"}
              </Button>
              {calAcik && !addedCal && (
                <div className="absolute right-0 z-30 mt-1 w-80 rounded-xl border bg-white p-3 shadow-lg dark:bg-gray-900 dark:border-white/15 space-y-2 text-left">
                  <div className="text-[12px] font-semibold">📅 Takvime ekle</div>
                  <label className="block text-[11px] text-muted-foreground">Yayın tarihi
                    <input type="date" value={calTarih} onChange={(e) => setCalTarih(e.target.value)} className="mt-1 w-full rounded-lg border bg-transparent px-2 py-1.5 text-[12.5px] text-foreground outline-none dark:border-white/15" />
                  </label>
                  <label className="block text-[11px] text-muted-foreground">WordPress yayını
                    <select value={calMod} onChange={(e) => setCalMod(e.target.value as typeof calMod)} className="mt-1 w-full rounded-lg border bg-transparent px-2 py-1.5 text-[12.5px] text-foreground outline-none dark:border-white/15">
                      <option value="onayli">Onaylı (önerilen) — o sabah onay ister</option>
                      <option value="otomatik">Otomatik — o sabah kendiliğinden yayınlar</option>
                      <option value="kapali">Kapalı — sadece plana yaz</option>
                    </select>
                  </label>
                  <p className="text-[10.5px] text-muted-foreground">Planlı gün 09:00'da çalışır. Taslak gönderdiysen o taslak yayına alınır; göndermediysen içerik görselleriyle yayınlanır.{!wpDurum.data?.bagli && calMod !== "kapali" ? " ⚠️ WordPress henüz bağlı değil — Ayarlar > WordPress." : ""}</p>
                  <div className="flex items-center gap-2">
                    <Button size="sm" className="text-xs bg-brand-500 hover:bg-brand-600" disabled={calBusy || !calTarih} onClick={() => void addToCalendar()}>{calBusy ? "Ekleniyor…" : "Ekle"}</Button>
                    <Button size="sm" variant="outline" className="text-xs" onClick={() => setCalAcik(false)}>Vazgeç</Button>
                  </div>
                </div>
              )}
            </div>
            <Button size="sm" variant="outline" className="text-xs gap-1" onClick={() => copyText("content", bodyContent)}>{copied === "content" ? <Check className="w-3.5 h-3.5" /> : null} Markdown</Button>
            <Button size="sm" variant="outline" className="text-xs gap-1" onClick={copyHtml}>{copied === "html" ? <Check className="w-3.5 h-3.5" /> : null} HTML Kopyala</Button>
            <Button size="sm" variant="outline" disabled={zipBusy} className="text-xs gap-1" onClick={() => void downloadWord()} title="Word belgesi (görseller gömülü) + görsel dosyaları + meta.txt — tek ZIP"><Download className="w-3.5 h-3.5" /> {zipBusy ? "Hazırlanıyor…" : "Word + Görseller (ZIP)"}</Button>
            {wpDurum.data?.bagli && (<>
              <Button size="sm" disabled={wpBusy} className="text-xs gap-1 bg-brand-500 hover:bg-brand-600" title="Görseller WP medya kütüphanesine yüklenir, yazı TASLAK olarak oluşturulur — yayına sen alırsın" onClick={() => void wpGonder(false)}>
                {wpBusy ? "Gönderiliyor…" : "WordPress'e Gönder (Taslak)"}
              </Button>
              <Button size="sm" variant="outline" disabled={wpBusy} className="text-xs gap-1 text-amber-600 border-amber-500/40" title="Doğrudan yayınlar — onay penceresi açılır" onClick={() => void wpGonder(true)}>
                Yayınla
              </Button>
            </>)}
            <input value={revNote} onChange={(e) => setRevNote(e.target.value)}
              placeholder='Revizyon notu — ör: "SSS kısalt, fiyat bölümü ekle"'
              className="text-xs px-2.5 py-1.5 rounded-lg border outline-none w-[260px]"
              style={{ borderColor: "rgba(139,92,246,0.4)" }} />
            {revNote.trim() ? (
              <Button size="sm" disabled={!!w?.running || regenerate.isPending} className="text-xs gap-1" style={{ background: "#e83e3c", color: "#fff" }}
                onClick={() => regenerate.mutate(revNote.trim())}
                title="Yazar sıfırdan yazmaz — mevcut içeriği bu nota göre günceller, kalan kısımlar korunur.">
                {regenerate.isPending || w?.running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "📝"} Notla Revize Et
              </Button>
            ) : (
              <Button size="sm" disabled={!!w?.running || regenerate.isPending} className="text-xs gap-1" style={{ background: C.cyan, color: "#141412" }}
                onClick={() => { void onayIste({ baslik: "Sıfırdan yeniden üretilsin mi?", aciklama: "Mevcut içeriğin üzerine yazılır. Küçük bir değişiklik istiyorsan bunun yerine revizyon notu yazman daha doğru.", onayYazisi: "Yeniden Üret", tehlikeli: true }).then((t) => { if (t) regenerate.mutate(undefined); }); }}>
                {regenerate.isPending || w?.running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Yeniden Üret
              </Button>
            )}
          </div>
        )}
      </div>

      {regenerate.isError && <p className="text-xs text-red-400 mb-2">{(regenerate.error as Error)?.message}</p>}
      {!w && <div className="text-center text-sm text-muted-foreground py-20">Yükleniyor…</div>}
      {w && !r && (
        <Card><CardContent className="p-10 text-center text-sm text-muted-foreground">
          {w.running ? <><Loader2 className="w-6 h-6 animate-spin mx-auto mb-3" /> İçerik üretiliyor — bitince burada görünecek.</> : "Bu brief için henüz içerik üretilmedi. İçerik Önerileri sayfasından \"İçerik Üret\"e bas."}
          {w.runError && <p className="text-red-400 mt-2 text-xs">{w.runError}</p>}
        </CardContent></Card>
      )}

      {w && r && (<>
        {/* KAPAK — her zaman içeriğin üstünde */}
        {cover && <img src={cover.imageUrl!} alt={cover.altText} className="w-full rounded-xl mb-4 object-cover" style={{ maxHeight: 420 }} />}

        <h1 className="text-2xl font-bold leading-snug">{r.title}</h1>
        <div className="flex items-center gap-2 flex-wrap mt-2 text-xs">
          <span className="px-2 py-0.5 rounded-full inline-flex items-center gap-1" style={{ background: `${C.amber}18`, color: "#b54708" }}><Target className="w-3 h-3" /> {w.brief.targetKeyword}{w.brief.volume > 0 && ` · ${fmt(w.brief.volume)}/ay`}</span>
          {w.brief.clusterName && <span className="px-2 py-0.5 rounded-full" style={{ background: "rgba(0,0,0,0.05)", color: "#70706c" }}>{w.brief.clusterName}</span>}
          {r.audit && <span className="px-2 py-0.5 rounded-full" style={{ background: r.audit.score >= 80 ? `${C.green}18` : `${C.amber}18`, color: r.audit.score >= 80 ? "#039855" : "#b54708" }}>🛡️ Denetçi {r.audit.score}/100</span>}
          <span className="text-muted-foreground">{fmt(r.wordCount)} kelime · 🎭 {r.toneUsed} · /{r.slug}</span>
        </div>
        <p className="text-sm text-muted-foreground mt-2 italic">{r.metaDescription} <button className="text-[10px] underline not-italic" onClick={() => copyText("meta", r.metaDescription)}>{copied === "meta" ? "✓" : "kopyala"}</button></p>

        {/* SEKMELER: İçerik | Brief & Analiz (kullanıcı isteği — sayfa daha clear) */}
        <div className="flex gap-1 mt-4 border-b">
          {([["icerik", "📄 İçerik"], ["analiz", "📊 Brief & Analiz"]] as const).map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)}
              className="px-4 py-2 text-sm font-medium border-b-2 transition"
              style={tab === k ? { borderColor: "#e83e3c", color: "#d42b29" } : { borderColor: "transparent", color: "#70706c" }}>
              {l}
            </button>
          ))}
        </div>

        {tab === "icerik" && (<>
        {/* 📋 YAYIN ADIMLARI — kullanıcının CMS'e gireceği alanlar, net ve kopyalanabilir */}
        <Card className="mt-4" style={{ borderColor: "rgba(139,92,246,0.35)" }}><CardContent className="p-4">
          <h3 className="text-sm font-semibold mb-3 text-brand-600 dark:text-brand-400">📋 Yayın Adımları — CMS'e girilecekler</h3>
          <div className="space-y-2 text-xs">
            {[
              { n: "1", l: "SEO Başlığı (title)", v: r.title, k: "st" },
              { n: "2", l: "Meta Açıklama", v: r.metaDescription, k: "sm" },
              { n: "3", l: "URL Slug", v: `/${r.slug}`, k: "ss" },
              { n: "4", l: "Schema (yapılandırılmış veri)", v: r.schemaSuggestion, k: "sc" },
            ].filter((s) => s.v).map((s) => (
              <div key={s.k} className="flex items-start gap-2">
                <span className="w-5 h-5 rounded-full text-white flex items-center justify-center shrink-0 text-[10px] font-bold" style={{ background: "#e83e3c" }}>{s.n}</span>
                <div className="min-w-0 flex-1">
                  <span className="font-semibold">{s.l}:</span> <span className="text-muted-foreground break-words">{s.v}</span>
                  <button className="ml-1.5 text-[10px] underline" onClick={() => copyText(s.k, s.v!)}>{copied === s.k ? "✓" : "kopyala"}</button>
                </div>
              </div>
            ))}
            <div className="flex items-start gap-2">
              <span className="w-5 h-5 rounded-full text-white flex items-center justify-center shrink-0 text-[10px] font-bold" style={{ background: "#e83e3c" }}>5</span>
              <div><span className="font-semibold">İçerik gövdesi:</span> <span className="text-muted-foreground">aşağıdaki içeriği üstteki kopyalama/indirme butonlarıyla al; [GÖRSEL-N] işaretçilerinin yerine ilgili görselleri koy ({r.imageBriefs.length} görsel).</span></div>
            </div>
          </div>
        </CardContent></Card>

        {/* 🏆 TEK KALİTE SKORU: checklist + rakip-normalize terim kapsaması birleşik */}
        {(() => {
          const cs = r.contentScore ?? null;
          const checklistPct = Math.round((okCount / Math.max(checklist.length, 1)) * 100);
          const overall = cs ? Math.round(checklistPct * 0.5 + cs.our * 0.5) : checklistPct;
          const col = overall >= 85 ? C.green : overall >= 60 ? C.amber : C.red;
          return (
        <Card className="my-5" style={{ borderColor: `${col}44` }}><CardContent className="p-4">
          <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
            <h3 className="text-sm font-semibold flex items-center gap-1.5"><Sparkles className="w-4 h-4" style={{ color: col }} /> Kalite Skoru <span className="text-xs text-muted-foreground font-normal">(checklist + rakip kıyası)</span></h3>
            <div className="flex items-center gap-4">
              {cs && (
                <span className="text-xs text-muted-foreground">
                  Terim kapsama: <b style={{ color: cs.our >= cs.target ? C.green : C.amber }}>{cs.our}</b> · Rakip ort: <b>{cs.competitorAvg}</b> · Hedef: <b className="text-brand-600 dark:text-brand-400">{cs.target}</b>
                </span>
              )}
              <span className="text-2xl font-bold tabular-nums" style={{ color: col }}>{overall}<span className="text-xs font-normal text-muted-foreground ml-1">/100</span></span>
            </div>
          </div>
          {cs && cs.missingTerms.length > 0 && (
            <div className="mb-3 text-xs">
              <span className="font-semibold" style={{ color: C.amber }}>Eksik terimler (rakipler kullanıyor, sende yok):</span>{" "}
              {cs.missingTerms.slice(0, 12).map((m) => (
                <span key={m.term} className="inline-block px-2 py-0.5 rounded-full mr-1 mb-1" style={{ background: `${C.amber}15`, color: "#b54708" }} title={`${m.competitorsUsing} rakip kullanıyor`}>{m.term}</span>
              ))}
              <span className="text-muted-foreground">→ revizyon notuna "şu terimleri doğal yerlerde işle" yazabilirsin.</span>
            </div>
          )}
          {r.plagiarism && r.plagiarism.hits.length > 0 && (
            <div className="mb-3 text-xs rounded-lg border p-2.5" style={{ borderColor: `${C.red}44`, background: `${C.red}08` }}>
              <span className="font-semibold" style={{ color: C.red }}>⚠️ Benzerlik uyarısı ({r.plagiarism.hits.length}/{r.plagiarism.checked} örnek):</span>
              {r.plagiarism.hits.map((h, i) => (
                <div key={i} className="mt-1 text-muted-foreground">"{h.sentence.slice(0, 80)}…" → <a href={h.url} target="_blank" rel="noopener noreferrer" className="underline decoration-dotted">{h.title || h.url}</a></div>
              ))}
            </div>
          )}
          {r.plagiarism && r.plagiarism.hits.length === 0 && (
            <div className="mb-3 text-xs" style={{ color: C.green }}>✓ Özgünlük kontrolü: {r.plagiarism.checked} örnek cümle SERP'te arandı, benzerlik bulunamadı.</div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {(["SEO", "GEO", "AEO"] as const).map((g) => (
              <div key={g}>
                <div className="text-[11px] font-bold mb-1.5" style={{ color: g === "SEO" ? C.blue : g === "GEO" ? C.purple : C.cyan }}>{g === "SEO" ? "🔍 SEO" : g === "GEO" ? "🤖 GEO" : "💬 AEO"}</div>
                <div className="space-y-1">{checklist.filter((c) => c.group === g).map((c, i) => (
                  <div key={i} className="flex items-start gap-1.5 text-xs">
                    <span className="shrink-0 mt-0.5" style={{ color: c.ok ? C.green : C.red }}>{c.ok ? "✓" : "✗"}</span>
                    <span className={c.ok ? "" : "text-muted-foreground"}>{c.label}{c.detail && <span className="text-muted-foreground"> · {c.detail}</span>}</span>
                  </div>
                ))}</div>
              </div>
            ))}
          </div>
        </CardContent></Card>
          );
        })()}

        {/* İÇERİK — kapak hariç görseller yerlerinde; ✏️ ile elle düzenlenebilir */}
        <Card><CardContent className="p-6">
          <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
            <span className="text-xs text-muted-foreground">{duzenle ? "Düzenleme modu — Markdown yazıyorsun: # başlık, ** kalın **, [GÖRSEL-N] görsel yeri" : ""}</span>
            <div className="flex items-center gap-2">
              {duzenle ? (<>
                <Button size="sm" variant="outline" className="text-xs" disabled={kaydet.isPending} onClick={() => setDuzenle(false)}>Vazgeç</Button>
                <Button size="sm" className="text-xs gap-1 bg-brand-500 hover:bg-brand-600" disabled={kaydet.isPending} onClick={() => kaydet.mutate()}>
                  {kaydet.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Kaydet
                </Button>
              </>) : (
                <Button size="sm" variant="outline" className="text-xs gap-1" title="Makaleyi elle düzenle — AI çağrılmaz, ücretsizdir. Kaydedince dışa aktarma ve WordPress gönderimi bu hâli kullanır."
                  onClick={() => { setTBaslik(r.title); setTMeta(r.metaDescription); setTGovde(bodyContent); setDuzenle(true); }}>
                  ✏️ Elle Düzenle
                </Button>
              )}
            </div>
          </div>
          {duzenle ? (
            <div className="space-y-3">
              <div>
                <label className="text-[11px] font-semibold text-muted-foreground">Başlık</label>
                <input value={tBaslik} onChange={(e) => setTBaslik(e.target.value)}
                  className="mt-1 w-full rounded-lg border bg-transparent px-3 py-2 text-sm font-semibold outline-none dark:border-white/15" />
              </div>
              <div>
                <label className="text-[11px] font-semibold text-muted-foreground">Meta Açıklama <span className="font-normal">({tMeta.length} karakter — ideal 140-160)</span></label>
                <textarea value={tMeta} onChange={(e) => setTMeta(e.target.value)} rows={2}
                  className="mt-1 w-full rounded-lg border bg-transparent px-3 py-2 text-[13px] outline-none dark:border-white/15" />
              </div>
              <div>
                <label className="text-[11px] font-semibold text-muted-foreground">İçerik (Markdown)</label>
                <textarea value={tGovde} onChange={(e) => setTGovde(e.target.value)}
                  className="mt-1 w-full rounded-lg border bg-transparent px-3 py-2 font-mono text-[13px] leading-relaxed outline-none dark:border-white/15"
                  style={{ minHeight: 560 }} spellCheck={false} />
              </div>
            </div>
          ) : (
            <WriterMd text={bodyContent} images={bodyImages} />
          )}
        </CardContent></Card>

        </>)}

        {tab === "analiz" && (<>
        {/* BRIEF & ANALİZ SEKMESİ: brief verileri + derin analiz + üretim istihbaratı */}
        <Card className="mt-4"><CardContent className="p-4 text-xs">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div>
              <div className="text-sm font-semibold">{w.brief.title}</div>
              <div className="text-muted-foreground mt-0.5">Hedef: <b style={{ color: C.amber }}>{w.brief.targetKeyword}</b>{w.brief.volume > 0 ? ` (${fmt(w.brief.volume)}/ay)` : ""} · {w.brief.clusterName ?? "cluster'sız"}</div>
              {(w.brief.supportingKeywords ?? []).length > 0 && <div className="text-muted-foreground mt-1">Destek: {(w.brief.supportingKeywords ?? []).join(", ")}</div>}
            </div>
            <Link href={`/planner-brief/${encodeURIComponent(w.brief.targetKeyword)}`}>
              <Button size="sm" variant="outline" className="text-xs">Tam Brief Detayı (varyasyon tablosu + 14 bölüm) →</Button>
            </Link>
          </div>
        </CardContent></Card>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4 text-xs">
          <Card><CardContent className="p-4">
            {r.linkPlan.length > 0 && <>
              <div className="text-[11px] font-semibold text-muted-foreground mb-1.5">🔗 Kurulan İç Linkler (cluster korumalı)</div>
              {r.linkPlan.map((l, i) => <div key={i} className="mb-1"><span style={{ color: C.green }}>"{l.anchor}"</span> → <a href={l.url} target="_blank" rel="noopener noreferrer" className="underline decoration-dotted text-muted-foreground">{shortUrl(l.url)}</a> <span className="text-muted-foreground">· {l.reason}</span></div>)}
            </>}
            {r.serpCompetitors.length > 0 && <>
              <div className="text-[11px] font-semibold text-muted-foreground mt-3 mb-1.5">🏆 İncelenen SERP Rakipleri</div>
              {r.serpCompetitors.map((c, i) => <div key={i} className="mb-0.5 truncate"><a href={c.url} target="_blank" rel="noopener noreferrer" className="underline decoration-dotted">{c.title || c.url}</a></div>)}
            </>}
          </CardContent></Card>
          <Card><CardContent className="p-4">
            {r.geoTactics.length > 0 && <>
              <div className="text-[11px] font-semibold mb-1.5 text-brand-600 dark:text-brand-400">🤖 GEO/AEO Taktikleri</div>
              {r.geoTactics.map((g, i) => <div key={i} className="text-muted-foreground mb-1">• {g}</div>)}
            </>}
            {r.schemaSuggestion && <div className="mt-2 text-muted-foreground"><b>Schema:</b> {r.schemaSuggestion}</div>}
            {r.audit && r.audit.notes.length > 0 && <>
              <div className="text-[11px] font-semibold mt-3 mb-1.5 text-success-600 dark:text-success-500">🛡️ Denetçi Notları</div>
              {r.audit.notes.map((n, i) => <div key={i} className="text-muted-foreground mb-1">{n}</div>)}
            </>}
          </CardContent></Card>
        </div>
        </>)}

        {tab === "icerik" && r.imageBriefs.length > 0 && (
          <div className="mt-4">
            <button onClick={() => setShowImgDetails(!showImgDetails)} className="text-[11px] font-semibold inline-flex items-center gap-1.5 text-brand-600 dark:text-brand-400">
              🖼️ Görsel Yönergeleri ({r.imageBriefs.length}) <span className="font-normal underline decoration-dotted">{showImgDetails ? "gizle" : "Detaylı Gör"}</span>
            </button>
            {showImgDetails && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 mt-2">
                {r.imageBriefs.map((im, i) => (
                  <div key={i} className="rounded-lg border p-2.5 text-xs" style={{ borderColor: "rgba(139,92,246,0.25)", background: "rgba(139,92,246,0.03)" }}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-semibold text-brand-600 dark:text-brand-400">{im.placeholder} · {im.aspectRatio}{im.placeholder === "[GÖRSEL-1]" ? " · KAPAK" : ""}</span>
                      <button className="text-[10px] underline" onClick={() => copyText(`img${i}`, im.prompt)}>{copied === `img${i}` ? "✓" : "prompt'u kopyala"}</button>
                    </div>
                    {im.imageUrl && <a href={im.imageUrl} target="_blank" rel="noopener noreferrer"><img src={im.imageUrl} alt={im.altText} loading="lazy" className="rounded-lg my-1.5 max-w-full" style={{ maxHeight: 200 }} /></a>}
                    {!im.imageUrl && im.imageError && <div className="my-1 text-[10px] text-warning-700 dark:text-warning-400">⚠️ {im.imageError}</div>}
                    <div className="text-muted-foreground">{im.position}</div>
                    <div className="mt-1 font-mono text-[10px] leading-snug text-gray-600 dark:text-gray-300">{im.prompt}</div>
                    <div className="mt-1 text-[10px]"><b>alt:</b> {im.altText}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </>)}
    </div>
  );
}
